import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { TextField, Button, Autocomplete } from '@anyemp/ui-kit';
import { DEFAULT_STAGE_COLOR } from '../../utils/colorUtils';
import { getTalentTypeOptions } from '../../utils/talentTypeUtils';

/**
 * StageForm
 *
 * `key` не спрашиваем вообще — сервер сам генерирует его из `name`
 * (talentStageController.generateUniqueKey). Сегодня ключ не резолвится ни
 * в одном рабочем пути (всё находит стадию по id) — это задел на будущее
 * (воронка, читающая стадии по стабильному коду, а не по переименовываемому
 * name), и заставлять автора стадии его придумывать ради этого не стоит.
 *
 * `stage_order` в форме не редактируется вообще — им управляет
 * drag-переупорядочивание на StageList (PUT /stages/order), которое при
 * каждом перетаскивании перенумеровывает весь список 1..N. Новая стадия
 * просто встаёт в конец, сервер сам подбирает следующий номер.
 *
 * Тумблера Active нет: is_active = false значит «стадия удалена», и это
 * делается только кнопкой удаления в списке (с проверкой на таланты).
 *
 * `talent_type` — тип талантов на этой стадии: талант всегда имеет тип своей
 * стадии. Поэтому менять тип можно, только пока стадия пуста (сервер это
 * проверяет) — иначе все таланты на ней разом сменили бы тип.
 */
const StageForm = ({ initialData = null, onSubmit, onCancel, submitLabel = 'Create' }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: DEFAULT_STAGE_COLOR,
    talent_type: ''
  });
  const [errors, setErrors] = useState({});

  const hasTalents = Boolean(initialData && (initialData.talents_count ?? 0) > 0);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        color: initialData.color || DEFAULT_STAGE_COLOR,
        talent_type: initialData.talent_type || ''
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.talent_type) newErrors.talent_type = 'Talent type is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    try {
      await onSubmit(formData);
      toast.success(initialData ? 'Stage updated successfully!' : 'Stage created successfully!');
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="max-w-md">
        <TextField
          label="Name *"
          name="name"
          fullWidth
          value={formData.name}
          onChange={handleChange}
          error={errors.name}
          placeholder="e.g. Video, Interview, Onboarding"
        />
      </div>

      <div className="max-w-md">
        <Autocomplete
          fullWidth
          label="Talent Type *"
          options={getTalentTypeOptions()}
          value={formData.talent_type}
          onChange={(value) => {
            setFormData(prev => ({ ...prev, talent_type: value || '' }));
            if (errors.talent_type) setErrors(prev => ({ ...prev, talent_type: '' }));
          }}
          placeholder="Select talent type..."
          error={!!errors.talent_type}
          helperText={errors.talent_type}
          disabled={hasTalents}
        />
        <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1">
          {hasTalents
            ? `The type cannot be changed while ${initialData.talents_count} talent(s) are on this stage.`
            : 'Talents on this stage always have this type: moving a talent here changes their type.'}
        </p>
      </div>

      {initialData && (
        <div>
          <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Key</label>
          <div className="text-light-text-primary dark:text-dark-text-primary font-mono text-sm">{initialData.key}</div>
        </div>
      )}

      <div className="max-w-md">
        <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Color</label>
        <input
          type="color"
          name="color"
          value={formData.color}
          onChange={handleChange}
          className="h-10 w-full rounded border border-light-border-primary dark:border-dark-border-primary bg-transparent"
        />
      </div>

      <div className="max-w-2xl">
        <TextField
          label="Description"
          name="description"
          multiline
          rows={3}
          fullWidth
          value={formData.description}
          onChange={handleChange}
          placeholder="Stage description (optional)"
        />
      </div>

      <div className="flex flex-col-reverse gap-3 pt-4 sm:flex-row sm:flex-wrap sm:justify-end [&>*]:w-full sm:[&>*]:w-auto">
        <Button type="button" variant="outlined" color="neutral" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" color="primary">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};

export default StageForm;
